import React from "react";
//#region types
import { FormEnums } from "models/enums";
import {
  EditableTableMoreActionMenuItemType,
  GenericFormSection,
  TableListFieldProps,
} from "models/propTypes/genericFormProps";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { TimeReportingProjectFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { TimeReportingProjectFormViewProps } from "models/propTypes/timeReportingProjectPropTypes";
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";
import { FormikProps, useFormikContext } from "formik";
//#endregion
//#region components
import { Grid } from "@mui/material";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import { SimpleTooltip } from "components/common";
import ProjectMemberStatusTags from "../projectMemberStatusTag";
import TimeReportingProjectMemberFormContainer from "./projectMembers/timeReportingProjectMemberFormContainer";
import { getMissingSelfBillingCategoryNamesForMember } from "./selfBillingValidation";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import variables from "theme/styles/_variables.module.scss";

const MemberMissingRateIcon: React.FC<{ member: TimeReportingProjectFormTypes.ProjectMember }> = ({ member }) => {
  const formik = useFormikContext<TimeReportingProjectFormTypes.Create>();
  const missingCategoryNames = getMissingSelfBillingCategoryNamesForMember(formik.values, member);
  if (missingCategoryNames.length === 0) return null;
  return (
    <SimpleTooltip
      text={LocalizationHelperInstance.getFormattedMessage(
        TranslationKeys.TimeReportingProject.SelfBillingMissingHourlyRateTooltip,
        undefined,
        { names: missingCategoryNames.join(", ") }
      )}
    >
      <div className={cssExports.tableToolTip}>
        <WarningRoundedIcon fontSize="small" htmlColor={variables.redV2} />
      </div>
    </SimpleTooltip>
  );
};

function useMembersFields(
  props: TimeReportingProjectFormViewProps
): GenericFormSection<TimeReportingProjectFormTypes.Create>[] {
  const getEmployeesMoreActions = (
    row: TimeReportingProjectFormTypes.ProjectMember,
    formikProps: FormikProps<TimeReportingProjectFormTypes.Create>
  ) => {
    const { values } = formikProps;
    const actions: EditableTableMoreActionMenuItemType[] = [];

    const isActive = DateHelper.withinIntervall(new Date(), row.startDate as Date, row.endDate as Date);
    const isProjectActive = DateHelper.withinIntervall(
      new Date(),
      values.projectStartDate as Date,
      values.projectEndDate as Date
    );

    if (isProjectActive) {
      if (isActive) {
        actions.push({
          description: LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.TimeReportingProject.InactivateMember
          ),
          icon: IconDefinitionConstants.WStop,
          onClickHandler: onDeactivateMember,
        });
      } else {
        actions.push({
          description: LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.TimeReportingProject.ActivateMember
          ),
          icon: IconDefinitionConstants.WCheck,
          onClickHandler: onActivateMember,
        });
      }
    }

    return actions;
  };

  const onDeactivateMember = async (
    rowIndex: number,
    row: TimeReportingProjectFormTypes.ProjectMember,
    formikProps: FormikProps<TimeReportingProjectFormTypes.Create>
  ) => {
    const { values, setFieldValue, setFieldTouched } = formikProps;

    const updatedRows = values.projectMembers?.map((m) =>
      m.id === row.id ? { ...m, endDate: DateHelper.addDays(new Date(), -1), isTimeReportingActive: false } : m
    );
    setFieldValue(FormFieldConstants.TimeReportingProject.Create.ProjectMembers, updatedRows);
    setFieldTouched(FormFieldConstants.TimeReportingProject.Create.ProjectMembers, true, false);
  };

  const onActivateMember = async (
    rowIndex: number,
    row: TimeReportingProjectFormTypes.ProjectMember,
    formikProps: FormikProps<TimeReportingProjectFormTypes.Create>
  ) => {
    const { values, setFieldValue, setFieldTouched } = formikProps;
    const today = new Date();
    const startDate = DateHelper.isBefore(today, new Date(row.startDate)) ? today : row.startDate;
    const endDate =
      row.endDate && DateHelper.isBefore(today, new Date(row.endDate)) ? row.endDate : values.projectEndDate;

    const updatedRows = values.projectMembers?.map((m) =>
      m.id === row.id ? { ...m, endDate: endDate, startDate: startDate, isTimeReportingActive: true } : m
    );
    setFieldValue(FormFieldConstants.TimeReportingProject.Create.ProjectMembers, updatedRows);
    setFieldTouched(FormFieldConstants.TimeReportingProject.Create.ProjectMembers, true, false);
  };

  return [
    {
      label: undefined,
      type: FormEnums.FormSectionType.FIELDGROUPSECTION,
      icon: IconDefinitionConstants.WPerson,
      innerFields: {
        projectMembers: {
          label: TranslationKeys.Common.Persons,
          type: FormEnums.FormFieldType.TABLELIST,
          descriptionText: TranslationKeys.Common.Users,
          extraProps: {
            resultTableProperties: {
              name: {
                formatter: (projectMember: TimeReportingProjectFormTypes.ProjectMember) => (
                  <Grid item container direction="column">
                    <Grid container direction="row" alignItems="center" spacing={1} wrap="nowrap">
                      <Grid item className={cssExports.tableListMainText}>{projectMember.name}</Grid>
                      <Grid item><MemberMissingRateIcon member={projectMember} /></Grid>
                    </Grid>
                    <Grid>{`${props.getProjectCategoriesForMember(projectMember)}`}</Grid>
                  </Grid>
                ),
              },
              timeReportingIsActive: {
                formatter: (value: TimeReportingProjectFormTypes.ProjectMember) => (
                  <ProjectMemberStatusTags member={value} />
                ),
              },
            },
            addItemForm: (values: TimeReportingProjectFormTypes.Create) => (
              <TimeReportingProjectMemberFormContainer
                excludeMemberIds={values.projectMembers?.map((member) => Number(member.id)) ?? []}
                currency={props.currency}
                isInternalProject={values?.isInternalProject ?? false}
                project={values}
                handleSubmit={props.onProjectMemberSubmitForm}
              />
            ),
            activeKey: FormFieldConstants.TimeReportingProject.ProjectMember.IsTimeReportingActive,
            moreActions: getEmployeesMoreActions,
            idFieldKey: FormFieldConstants.TimeReportingProject.ProjectMember.Id,
            icon: IconDefinitionConstants.WBusinessCard,
            entityText: TranslationKeys.Common.Person,
            fullWidth: true,
            maxWidth: "md",
            editItemButtonText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Done),
            confirmCloseOnX: true,
          } as TableListFieldProps<TimeReportingProjectFormTypes.ProjectMember>,
        },
      },
    },
  ];
}

export default useMembersFields;
